import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Place, RouteSuggestion } from '../types';
import { getConfig } from '../services/routexApi';
import { loadStopPhotoUrl, peekStopPhotoUrl } from '../utils/chatStopPhotoCache';
import { placesFromRouteSuggestion } from '../utils/routeSuggestionPlaces';

/** One short line: why this stop is on the route. */
function stopWhySentence(place: Place): string {
  const pref =
    place.found_for_preferences?.find(Boolean) || place.found_for_preference;
  if (pref) {
    return `Chosen because you asked for "${pref}".`;
  }
  const type = place.types?.find(
    (t) => !['point_of_interest', 'establishment', 'premise', 'geocode'].includes(t),
  );
  if (type) {
    return `Adds a relevant ${type.replace(/_/g, ' ')} along the way.`;
  }
  if (place.rating != null && place.rating >= 4.0) {
    return `Highly rated (${place.rating.toFixed(1)}), so it is a dependable pause.`;
  }
  return 'Fits neatly between your start and destination.';
}

interface HorizontalStopStripProps {
  places: Place[];
  routeIndex: number;
  apiKey: string | null;
  onOpenPlaceDetails?: (place: Place) => void;
  canRemoveStop?: (place: Place, routeIndex: number) => boolean;
  onRemoveStop?: (place: Place, routeIndex: number) => void;
}

const HorizontalStopStrip: React.FC<HorizontalStopStripProps> = ({
  places,
  routeIndex,
  apiKey,
  onOpenPlaceDetails,
  canRemoveStop,
  onRemoveStop,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [resolvedPhotos, setResolvedPhotos] = useState<Record<string, string>>({});
  const drag = useRef({
    active: false,
    startX: 0,
    scrollLeft: 0,
    pointerId: -1,
    dragged: false,
  });

  const cardKey = (place: Place, index: number) => `${place.place_id}::${index}`;

  const placesSignature = places
    .map(
      (p, i) =>
        `${p.place_id}|${p.location.lat.toFixed(5)},${p.location.lng.toFixed(5)}|${i}|${p.name ?? ''}`,
    )
    .join('~');

  useLayoutEffect(() => {
    const fromPeek: Record<string, string> = {};
    places.forEach((place, i) => {
      const key = cardKey(place, i);
      const url = peekStopPhotoUrl(place, apiKey);
      if (url) fromPeek[key] = url;
    });
    setResolvedPhotos(fromPeek);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placesSignature, apiKey]);

  useEffect(() => {
    let cancelled = false;
    const pending: Array<{ key: string; place: Place }> = [];
    places.forEach((place, i) => {
      const key = cardKey(place, i);
      if (peekStopPhotoUrl(place, apiKey)) return;
      pending.push({ key, place });
    });
    if (!pending.length) return;

    const run = async () => {
      const concurrency = 6;
      for (let i = 0; i < pending.length; i += concurrency) {
        if (cancelled) return;
        const batch = pending.slice(i, i + concurrency);
        const results = await Promise.all(
          batch.map(async ({ key, place }) => {
            const url = await loadStopPhotoUrl(place, apiKey);
            return { key, url };
          }),
        );
        if (cancelled) return;
        setResolvedPhotos((prev) => {
          const next = { ...prev };
          for (const { key, url } of results) {
            if (url) next[key] = url;
          }
          return next;
        });
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placesSignature, apiKey]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    el.setPointerCapture(e.pointerId);
    drag.current = {
      active: true,
      startX: e.clientX,
      scrollLeft: el.scrollLeft,
      pointerId: e.pointerId,
      dragged: false,
    };
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!drag.current.active || !scrollRef.current) return;
    const dx = Math.abs(e.clientX - drag.current.startX);
    if (dx > 6) drag.current.dragged = true;
    scrollRef.current.scrollLeft =
      drag.current.scrollLeft - (e.clientX - drag.current.startX);
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (!scrollRef.current) return;
    if (drag.current.pointerId === e.pointerId) {
      try {
        scrollRef.current.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    }
    drag.current.active = false;
  }, []);

  return (
    <div
      ref={scrollRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory scroll-smooth cursor-grab active:cursor-grabbing select-none no-scrollbar"
    >
      {places.map((place, i) => {
        const key = cardKey(place, i);
        const img = resolvedPhotos[key] ?? null;
        const why = stopWhySentence(place);
        const removable = canRemoveStop?.(place, routeIndex) ?? false;

        return (
          <div
            key={`${place.place_id}-${i}`}
            className="relative snap-start shrink-0 w-[148px] rounded-xl border border-gray-200/80 bg-gradient-to-b from-white to-gray-50/90 shadow-sm overflow-hidden"
          >
            {removable && (
              <button
                type="button"
                aria-label={`Remove ${place.name} from route`}
                className="absolute top-1 right-1 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/70"
                onPointerDown={(e) => {
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveStop?.(place, routeIndex);
                }}
              >
                <span className="material-symbols-outlined text-[16px] leading-none">close</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (drag.current.dragged) return;
                onOpenPlaceDetails?.(place);
              }}
              className="w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-inset transition-transform active:scale-[0.98]"
            >
              <div className="aspect-[4/3] bg-gray-200 relative">
                {img ? (
                  <img
                    src={img}
                    alt=""
                    className="w-full h-full object-cover pointer-events-none"
                    loading="eager"
                    decoding="async"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <span className="material-symbols-outlined text-3xl">image</span>
                  </div>
                )}
                <span className="absolute top-1.5 left-1.5 text-[10px] font-bold text-white bg-black/55 px-1.5 py-0.5 rounded-md">
                  {i + 1}
                </span>
              </div>
              <div className="p-2">
                <p className="text-xs font-semibold text-gray-900 line-clamp-2 leading-snug">
                  {place.name}
                </p>
                {place.rating != null && Number.isFinite(place.rating) && (
                  <p className="mt-0.5 flex items-center gap-0.5 text-amber-600 text-[10px] font-semibold">
                    {place.rating.toFixed(1)}
                    <span
                      className="material-symbols-outlined filled text-[12px]"
                      style={{ fontVariationSettings: "'FILL' 1" }}
                    >
                      star
                    </span>
                  </p>
                )}
                <p className="text-[10px] text-gray-600 mt-1 line-clamp-3 leading-relaxed">
                  {why}
                </p>
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
};

export interface ChatRouteSuggestionsProps {
  suggestions: RouteSuggestion[];
  /** Same as tapping a stop in Directions: place details only, no agent. */
  onOpenPlaceDetails?: (place: Place) => void;
  canRemoveStop?: (place: Place, routeIndex: number) => boolean;
  onRemoveStop?: (place: Place, routeIndex: number) => void;
  /** Switch to map, select this route in the ribbon, and expand route details. */
  onOpenMap?: (routeIndex: number) => void;
}

export const ChatRouteSuggestions: React.FC<ChatRouteSuggestionsProps> = ({
  suggestions,
  onOpenPlaceDetails,
  canRemoveStop,
  onRemoveStop,
  onOpenMap,
}) => {
  const [apiKey, setApiKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getConfig()
      .then((c) => {
        if (!cancelled && c.google_maps_api_key) setApiKey(c.google_maps_api_key);
      })
      .catch(() => {
        if (!cancelled) setApiKey(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!suggestions.length) return null;

  const showRemoveHint = canRemoveStop != null && onRemoveStop != null;

  return (
    <div className="mt-4 space-y-4">
      {suggestions.map((suggestion, routeIndex) => {
        const places = placesFromRouteSuggestion(suggestion);
        if (!places.length) return null;
        const label =
          suggestion.name?.trim() ||
          (suggestions.length > 1 ? `Route ${routeIndex + 1}` : 'Your route');
        const minutes = Math.round(suggestion.total_time || 0);
        const meta =
          minutes > 0
            ? `~${minutes} min · ${places.length} stop${places.length === 1 ? '' : 's'}`
            : `${places.length} stop${places.length === 1 ? '' : 's'}`;

        return (
          <div
            key={`route-${routeIndex}-${suggestion.polyline?.slice(0, 20) ?? routeIndex}`}
            className="rounded-2xl border border-indigo-100/80 bg-gradient-to-br from-indigo-50/90 via-white to-sky-50/70 p-3 shadow-sm"
          >
            <div className="flex items-start justify-between gap-2 mb-2 px-0.5">
              <div>
                <p className="text-xs font-bold text-indigo-900 tracking-wide uppercase">
                  {label}
                </p>
                <p className="text-[11px] text-indigo-700/80 mt-0.5">{meta}</p>
              </div>
              <button
                type="button"
                onClick={() => onOpenMap?.(routeIndex)}
                className="group relative shrink-0 rounded-full p-1.5 text-indigo-500 hover:bg-indigo-100/80 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/70"
                title="Open route on map"
                aria-label="Open route on map"
              >
                <span className="material-symbols-outlined text-xl">map</span>
                <span className="pointer-events-none absolute -bottom-7 right-0 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-[10px] font-medium text-white opacity-0 shadow-sm transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100">
                  View map
                </span>
              </button>
            </div>
            <HorizontalStopStrip
              places={places}
              routeIndex={routeIndex}
              apiKey={apiKey}
              onOpenPlaceDetails={onOpenPlaceDetails}
              canRemoveStop={canRemoveStop}
              onRemoveStop={onRemoveStop}
            />
            <p className="text-[10px] text-gray-500 mt-2 px-0.5">
              {showRemoveHint
                ? 'Tap a stop for details. The X button removes it from your route and updates the map.'
                : 'Drag sideways to explore · tap a stop for details'}
            </p>
          </div>
        );
      })}
    </div>
  );
};
